import React from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import ReactSelect from 'react-select'
import tw, { theme } from 'twin.macro'
import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import { useConfig } from '../ConfigProvider'
import { useForm } from '../Form/context'
import { useInputGroup } from '../InputGroup/context'
import { HiCheck, HiX } from 'react-icons/hi'
import { HiChevronDown } from "react-icons/hi2";
import Spinner from '../Spinner'
import { CONTROL_SIZES, SIZES } from '../utils/constant'
import ChevronDown from 'assets/icons/chevronDown'

const DefaultOption = ({
    innerProps,
    label,
    selectProps,
    isSelected,
    isDisabled,
    isFocused
}) => {
    const { themeColor  } = selectProps
    return (
        <div
            className={
                classNames(
                    'select-option', 
                    isSelected && 'selected', 
                    isDisabled && 'disabled',
                    isFocused && 'focused'
                )
            }
            {...innerProps}
        >
            <span className="ml-2">{label}</span>
            {isSelected && (
                <HiCheck
                    className={`text-${themeColor} dark:text-white text-xl`}
                />
            )}
        </div>
    )
}

const DefaultDropdownIndicator = () => {
    return (
        <div className="select-dropdown-indicator w-[32px] flex items-center justify-center text-gray-600">
            {/* Custom SVG Icon */}
            {/* <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-gray-600"
            >
                <path
                    d="M6 9L12 15L18 9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg> */}
            <ChevronDown/>
        </div>
    )
}

const DefaultClearIndicator = (props) => {
    const {
        innerProps: { ref, ...restInnerProps },
    } = props
    return (
        <div {...restInnerProps} ref={ref}>
            <div className="select-clear-indicator">
                <HiX />
            </div>
        </div>
    )
}

const DefaultLoadingIndicator = ({ selectProps }) => {
    const { themeColor } = selectProps
    return (
        <Spinner className={`select-loading-indicatior text-${themeColor}`} />
    )
}

const Select = React.forwardRef((props, ref) => {
    const {
        size,
        style,
        styles: stylesProp,
        className,
        form,
        field,
        components,
        componentAs: Component,
        ...rest
    } = props

    const { themeColor, controlSize, primaryColorLevel, mode } = useConfig()
    const formControlSize = useForm()?.size
    const inputGroupSize = useInputGroup()?.size

    const selectSize = size || inputGroupSize || formControlSize || controlSize

    const twColor = theme`colors`
    const twHeight = theme`height`

    let isInvalid = false

    if (!isEmpty(form)) {
        const { touched, errors } = form

        const touchedField = get(touched, field.name)
        const errorField = get(errors, field.name)

        isInvalid = touchedField && errorField
    }

    const getBoxShadow = (state) => {
        const shadaowBase = '0 0 0 1px '

        if (isInvalid) {
            return shadaowBase + twColor.red['500']
        }

        if (state.isFocused) {
            return shadaowBase + twColor[themeColor][primaryColorLevel]
        }

        return 'none'
    }

    const defaultKeys = ['control', 'placeholder', 'input', 'menu']
    const allKeys = Array.from(new Set([...defaultKeys, ...Object.keys(stylesProp || {})]))

    const styles = {}
    allKeys.forEach((key) => {
        styles[key] = (provided, state) => {
            let base = provided
            
            if (key === 'control') {
                base = {
                    ...provided,
                    height: twHeight[CONTROL_SIZES[selectSize]],
                    minHeight: twHeight[CONTROL_SIZES[selectSize]],
                    '&:hover': {
                        boxShadow: getBoxShadow(state),
                        cursor: 'pointer',
                    },
                    boxShadow: getBoxShadow(state),
                    borderRadius: tw`rounded-md`.borderRadius,
                    borderColor: twColor.gray['300'],
                    ...(isInvalid ? { borderColor: twColor.red['500'] } : {}),
                }
            } else if (key === 'placeholder') {
                base = {
                    ...provided,
                    color: '#4B5563',
                    fontWeight: 400,
                }
            } else if (key === 'input') {
                base = {
                    ...provided,
                    input: {
                        outline: 'none',
                        outlineOffset: 0,
                        boxShadow: 'none !important',
                    },
                }
            } else if (key === 'menu') {
                base = {
                    ...provided,
                    zIndex: 50,
                }
            }
            
            if (stylesProp && stylesProp[key]) {
                if (typeof stylesProp[key] === 'function') {
                    return stylesProp[key](base, state)
                }
                return { ...base, ...stylesProp[key] }
            }
            
            return base
        }
    })
    
    Object.assign(styles, style)
    

    const selectClass = classNames('select', `select-${selectSize}`, className)

    return (
        <Component
            className={selectClass}
            classNamePrefix={'select'}
            ref={ref}
            styles={styles}
            theme={(theme) => ({
                ...theme,
                colors: {
                    ...theme.colors,
                    neutral20:
                        mode === 'dark'
                            ? twColor.gray['600']
                            : twColor.gray['300'],
                    neutral30:
                        mode === 'dark'
                            ? twColor.gray['600']
                            : twColor.gray['300'],
                    neutral80: twColor.gray['700'],
                    neutral10: 
                        mode === 'dark'
                            ? twColor.gray['600']
                            : twColor.gray['300'],
                    primary25: twColor[themeColor]['50'],
                    primary50: twColor[themeColor]['100'],
                    primary: twColor[themeColor][primaryColorLevel],
                },
            })}
            themeColor={`${themeColor}-${primaryColorLevel}`}
            components={{
                IndicatorSeparator: () => null,
                Option: DefaultOption,
                LoadingIndicator: DefaultLoadingIndicator,
                DropdownIndicator: rest.dropDownIndicator?rest.dropDownIndicator:DefaultDropdownIndicator,
                ClearIndicator: DefaultClearIndicator,
                ...components,
            }}
            {...field}
            {...rest}
        />
    )
})

Select.propTypes = {
    size: PropTypes.oneOf([SIZES.LG, SIZES.MD, SIZES.SM]),
    componentAs: PropTypes.elementType,
    // dropDownIndicator: PropTypes.ReactElementLike
}

Select.defaultProps = {
    componentAs: ReactSelect,
}

export default Select
